import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Box, Button, Callout, Card, Dialog, Flex, Heading, ScrollArea, Text } from '@radix-ui/themes';
import { toast } from 'react-hot-toast';
import { OnboardingScript, onboardingScriptToText, splitScriptNotes } from '../utils/onboardingScript';
import {
  ClinicianBrief,
  CLINICIAN_BRIEF_PHASE_COLORS,
  engagementTitle,
  formatBriefDate,
} from '../utils/clinicianBrief';
import { patientApi } from '../services/api';

/** The small blue uppercase headline over each block (clinician brief, onboarding script). */
const SectionLabel: React.FC<{ children: React.ReactNode; testId?: string }> = ({ children, testId }) => (
  <Text size="1" weight="bold" color="blue" className="uppercase tracking-wide" data-testid={testId}>
    {children}
  </Text>
);

// PHA-626: the onboarding script dialog. On top, a brief for the clinician (status + an
// AI-written overview, not read to the patient); below, the script the caller reads.
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  script: OnboardingScript | null;
  /** Why some sections use general wording (no plain-English plan). */
  notice?: string | null;
  /** Loads the clinician brief; without it only the script is shown. */
  patientId?: string | number | null;
}

/** A script paragraph with any caller note styled apart from the spoken text. */
const ScriptText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {splitScriptNotes(text).map((seg, i) =>
      seg.note ? (
        <Text
          key={i}
          size="1"
          color="gray"
          className="italic"
          style={{ background: 'var(--amber-a3)', borderRadius: 4, padding: '0 4px' }}
          title="A note for you. Not read to the patient."
          data-testid="onboarding-script-caller-note"
        >
          {seg.text}
        </Text>
      ) : (
        <React.Fragment key={i}>{seg.text}</React.Fragment>
      ),
    )}
  </>
);

const BriefList: React.FC<{ title: string; items: string[]; testId: string }> = ({ title, items, testId }) =>
  items.length === 0 ? null : (
    <Box className="mt-2" data-testid={testId}>
      <Text as="p" size="2" weight="bold">{title}</Text>
      <ul className="list-disc pl-5 space-y-1">
        {items.map((b, i) => (
          <li key={i}><Text size="2">{b}</Text></li>
        ))}
      </ul>
    </Box>
  );

const ClinicianBriefBox: React.FC<{ patientId: string | number; open: boolean }> = ({ patientId, open }) => {
  const [brief, setBrief] = useState<ClinicianBrief | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  const load = useCallback(async () => {
    setState('loading');
    try {
      setBrief(await patientApi.getClinicianBrief(String(patientId)));
      setState('loaded');
    } catch (err) {
      console.error('PHA-626: clinician brief failed:', err);
      setState('error');
    }
  }, [patientId]);

  useEffect(() => {
    if (open) void load();
    else {
      setBrief(null);
      setState('idle');
    }
  }, [open, load]);

  return (
    <Card className="mb-4" style={{ background: 'var(--blue-a2)' }} data-testid="clinician-brief">
      <Flex align="center" justify="between" gap="2" wrap="wrap">
        <SectionLabel testId="clinician-brief-label">Clinician brief</SectionLabel>
        {brief && (
          <Badge color={CLINICIAN_BRIEF_PHASE_COLORS[brief.phase]} size="2" data-testid="clinician-brief-status">
            {brief.phase_label}
          </Badge>
        )}
      </Flex>
      {state === 'loaded' && brief?.program_start_date && (
        <Text as="p" size="2" className="mt-2" data-testid="clinician-brief-joined">
          <Text weight="bold">Joined the program:</Text> {formatBriefDate(brief.program_start_date)}
          {brief.program_start_source === 'care_plan' && (
            <Text color="gray"> (first approved care plan; no consent date on file)</Text>
          )}
        </Text>
      )}
      {state === 'loading' && (
        <Text as="p" size="2" color="gray" className="mt-2">Preparing the brief…</Text>
      )}
      {state === 'error' && (
        <Flex align="center" gap="2" className="mt-2">
          <Text size="2" color="red">Could not prepare the brief.</Text>
          <Button size="1" variant="soft" onClick={() => void load()}>Try again</Button>
        </Flex>
      )}
      {state === 'loaded' && brief && (
        <Box>
          {brief.overview && <Text as="p" size="2" className="mt-2">{brief.overview}</Text>}
          <BriefList
            title="Suggested focus"
            items={brief.focus_conditions.map((f) => (f.reason ? `${f.condition}: ${f.reason}` : f.condition))}
            testId="clinician-brief-focus"
          />
          <BriefList
            title="Medications to note"
            items={brief.medications_to_watch.map((m) => (m.reason ? `${m.name}: ${m.reason}` : m.name))}
            testId="clinician-brief-meds"
          />
          <BriefList title="Care plan" items={brief.care_plan} testId="clinician-brief-care-plan" />
          <BriefList title={engagementTitle(brief)} items={brief.engagement} testId="clinician-brief-engagement" />
        </Box>
      )}
    </Card>
  );
};

const OnboardingScriptDialog: React.FC<Props> = ({ open, onOpenChange, script, notice, patientId }) => {
  const handleCopy = async () => {
    if (!script) return;
    try {
      await navigator.clipboard.writeText(onboardingScriptToText(script));
      toast.success('Script copied.');
    } catch {
      toast.error('Could not copy the script.');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: 720 }}>
        <Flex align="center" justify="between" gap="2">
          <Dialog.Title className="mb-0">{script?.title ?? 'Onboarding Script'}</Dialog.Title>
          <Button size="2" variant="soft" onClick={handleCopy} data-testid="onboarding-script-copy">Copy script</Button>
        </Flex>
        <ScrollArea type="auto" scrollbars="vertical" style={{ maxHeight: '70vh' }} className="mt-3">
          <Box className="pr-3">
            {patientId !== undefined && patientId !== null && patientId !== '' && (
              <ClinicianBriefBox patientId={patientId} open={open} />
            )}
            {notice && (
              <Callout.Root color="orange" className="mb-3">
                <Callout.Text>{notice}</Callout.Text>
              </Callout.Root>
            )}
            {/* Zack, 2026-11-03: a headline like the brief's, so the two blocks read as a pair. */}
            <Box className="mb-2">
              <SectionLabel testId="onboarding-script-label">Onboarding script</SectionLabel>
            </Box>
            <Box className="space-y-4" data-testid="onboarding-script-body">
              {script?.sections.map((s) => (
                <Box key={s.id} data-testid={`onboarding-script-section-${s.id}`}>
                  <Heading size="3" className="mb-1">{s.heading}</Heading>
                  {s.paragraphs.map((p, i) => (
                    <Text key={i} as="p" size="2" className="mb-2"><ScriptText text={p} /></Text>
                  ))}
                  {s.bullets.length > 0 && (
                    <ul className="list-disc pl-5 space-y-1">
                      {s.bullets.map((b, i) => (
                        <li key={i}><Text size="2"><ScriptText text={b} /></Text></li>
                      ))}
                    </ul>
                  )}
                  {s.closing && (
                    <Text as="p" size="2" weight="bold" className="mt-2">{s.closing}</Text>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </ScrollArea>
        <Flex justify="end" className="mt-4">
          <Dialog.Close>
            <Button variant="soft" color="gray">Close</Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default OnboardingScriptDialog;
